import React, { useState } from 'react';
import { useAuth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  BookText, Download, FileText, FileType2, Loader2, ShieldAlert, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { num: 1,  title: 'Overview & Purpose' },
  { num: 2,  title: 'Tech Stack' },
  { num: 3,  title: 'Repository Layout' },
  { num: 4,  title: 'Environments & Deployment' },
  { num: 5,  title: 'Architecture & Conventions' },
  { num: 6,  title: 'Authentication & RBAC' },
  { num: 7,  title: 'Module Catalog' },
  { num: 8,  title: 'Core Workflows (Lead lifecycle, Leave chain, Payroll, Finance…)' },
  { num: 9,  title: 'Data Model — Key Collections' },
  { num: 10, title: 'Third-Party Integrations' },
  { num: 11, title: 'Background Jobs & Schedulers' },
  { num: 12, title: 'Notifications' },
  { num: 13, title: 'Common Patterns & Gotchas' },
  { num: 14, title: 'Local Setup for New Developers' },
  { num: 15, title: 'Where to Look First — Cheat Sheet' },
  { num: 16, title: 'Glossary' },
];

const DeveloperGuidePage = () => {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(null);

  const isAuthorized = user?.role === 'super_admin' || user?.role === 'admin';

  const download = async (filename) => {
    setDownloading(filename);
    try {
      const token = sessionStorage.getItem('clt_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/exports/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally {
      setDownloading(null);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="flex items-center gap-3 py-12 justify-center text-muted-foreground">
            <ShieldAlert className="h-6 w-6" />
            <span>Super Admin / Admin only.</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="developer-guide-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="bg-slate-900 dark:bg-slate-700 p-3 rounded-lg">
            <BookText className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Developer Guide</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Comprehensive onboarding documentation for incoming developers.
              16 sections covering architecture, workflows, data models, integrations,
              and conventions. <span className="font-medium">No credentials are included.</span>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-2 border-rose-200 dark:border-rose-900/50 hover:border-rose-400 transition-colors">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileType2 className="h-5 w-5 text-rose-600" />
                PDF Version
              </CardTitle>
              <Badge variant="secondary" className="bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300">Printable</Badge>
            </div>
            <CardDescription>
              Styled A4 PDF — ideal for printing, handouts, or attaching to onboarding emails.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => download('DEVELOPER_GUIDE.pdf')}
              disabled={downloading === 'DEVELOPER_GUIDE.pdf'}
              className="w-full bg-rose-600 hover:bg-rose-700"
              data-testid="download-pdf-btn"
            >
              {downloading === 'DEVELOPER_GUIDE.pdf'
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              Download PDF (~70 KB)
            </Button>
          </CardContent>
        </Card>

        <Card className="border-2 border-emerald-200 dark:border-emerald-900/50 hover:border-emerald-400 transition-colors">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-5 w-5 text-emerald-600" />
                Markdown Version
              </CardTitle>
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">Editable</Badge>
            </div>
            <CardDescription>
              Raw .md — view in GitHub, edit in any IDE, or paste into Notion / Confluence.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => download('DEVELOPER_GUIDE.md')}
              disabled={downloading === 'DEVELOPER_GUIDE.md'}
              variant="outline"
              className="w-full border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300"
              data-testid="download-md-btn"
            >
              {downloading === 'DEVELOPER_GUIDE.md'
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              Download Markdown (~40 KB)
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Contents</CardTitle>
          <CardDescription>16 sections + 3 appendices (PR checklist, API smoke test, recent changes).</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-1.5 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.num} className="flex gap-3 items-start hover:bg-muted/40 -mx-2 px-2 py-1 rounded">
                <span className="text-muted-foreground font-mono w-6 text-right shrink-0">{s.num}.</span>
                <span>{s.title}</span>
              </li>
            ))}
            <li className="flex gap-3 items-start text-muted-foreground pt-2 border-t mt-3">
              <span className="font-mono w-6 text-right shrink-0">A.</span>
              <span>Standard PR Checklist</span>
            </li>
            <li className="flex gap-3 items-start text-muted-foreground">
              <span className="font-mono w-6 text-right shrink-0">B.</span>
              <span>Quick API Smoke Test</span>
            </li>
            <li className="flex gap-3 items-start text-muted-foreground">
              <span className="font-mono w-6 text-right shrink-0">C.</span>
              <span>Recent Major Changes (June 2026)</span>
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card className="bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
        <CardContent className="py-4 flex gap-3 items-start text-sm">
          <ExternalLink className="h-4 w-4 text-amber-700 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="text-amber-900 dark:text-amber-200">
            The document is regenerated from <code className="font-mono bg-amber-100 dark:bg-amber-900/50 px-1.5 py-0.5 rounded text-xs">/app/DEVELOPER_GUIDE.md</code>.
            Run <code className="font-mono bg-amber-100 dark:bg-amber-900/50 px-1.5 py-0.5 rounded text-xs">python3 backend/scripts/render_developer_guide_pdf.py</code> after editing
            the markdown to refresh the PDF.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeveloperGuidePage;
